import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {Loader2} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Si se indica, fuerza que el perfil sea de este tipo de cuenta */
  requireType?: 'empresa' | 'colaborador'
}

/**
 * Protege rutas que requieren sesión de Lumi (Empresa / Colaborador).
 * Opcionalmente valida el tipo de cuenta consultando la colección profiles.
 *
 * IMPORTANTE: usa useAuth para esperar a que el SDK rehidrate la sesión
 * (isLoading) tras una recarga completa de la página. Sin esto, el guard
 * leería un estado no autenticado momentáneo y rebotaría al usuario al login.
 *
 * Además aplica una breve "ventana de gracia" cuando la sesión aún no aparece,
 * para tolerar el desfase entre el login SPA y la emisión del evento
 * onAuthChange del SDK (relevante dentro del iframe del editor de Lumi).
 */
function ProtectedRoute({ children, requireType }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const [typeStatus, setTypeStatus] = useState<'checking' | 'ok' | 'denied'>('checking')
  // Ventana de gracia para esperar el evento de sesión tras un login SPA.
  const [graceElapsed, setGraceElapsed] = useState(false)

  // Arranca un temporizador corto la primera vez que no hay sesión resuelta.
  useEffect(() => {
    if (isLoading) return
    if (isAuthenticated && user) return
    const t = setTimeout(() => setGraceElapsed(true), 1500)
    return () => clearTimeout(t)
  }, [isLoading, isAuthenticated, user])

  useEffect(() => {
    let active = true

    // Esperar a que el SDK termine de resolver la sesión.
    if (isLoading) return

    // Sin sesión válida: esperamos a que pase la ventana de gracia antes
    // de denegar, por si el evento onAuthChange llega con un pequeño retraso.
    if (!isAuthenticated || !user) {
      if (graceElapsed && active) setTypeStatus('denied')
      return
    }

    // No se exige tipo de cuenta concreto.
    if (!requireType) {
      if (active) setTypeStatus('ok')
      return
    }

    const check = async () => {
      try {
        const { list } = await lumi.entities.profiles.list({
          filter: { userId: user.userId },
          limit: 1,
        })
        const type = list?.[0]?.accountType
        if (active) setTypeStatus(type === requireType ? 'ok' : 'denied')
      } catch (e) {
        console.error('Error verificando perfil:', e)
        // Si falla la verificación de tipo pero hay sesión, permitimos el acceso.
        if (active) setTypeStatus('ok')
      }
    }
    check()

    return () => {
      active = false
    }
  }, [isAuthenticated, isLoading, user, requireType, graceElapsed])

  // Mientras el SDK resuelve la sesión, esperamos la ventana de gracia o
  // validamos el tipo de cuenta, mostramos un loader.
  if (isLoading || typeStatus === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 size={28} className="animate-spin text-amber-500" />
      </div>
    )
  }

  if (typeStatus === 'denied') {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
